/**
 * Candidate API Service - Standardized Response Format
 * FIX #2: Now matches cv.js response format for consistency
 */

import http from '../client';
import { ENDPOINTS } from '../config';

// Standardized response handlers (same as cv.js)
const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

// ==================== JWT-based endpoints (recommended) ====================

/**
 * Get current user's profile using JWT token.
 *
 * hrSystemServer answers GET /chat/profile/me with an envelope —
 * `{ found, message, candidate: {...} }` — not the candidate itself. Callers
 * kept reading `data.id` / `data.photoUrl` off that envelope and getting
 * undefined (which broke profile-photo upload and made CV validation report
 * every candidate field as missing), so the candidate is unwrapped here, once.
 *
 * @returns {Promise<Object>} { success, data: candidate|null, status, message }
 *                            or { success: false, error, status, requiresAuth }
 */
export const getMyProfile = async () => {
  try {
    const response = await http.get(ENDPOINTS.CANDIDATES.ME);
    const result = handleApiSuccess(response);
    const payload = result.data;
    // `found: false` means there is no candidate record yet — report no data
    // rather than handing back an envelope that looks like an empty profile.
    if (payload && typeof payload === 'object' && 'candidate' in payload) {
      return { ...result, data: payload.candidate || null };
    }
    return result;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update current user's profile using JWT token
 * @param {Object} profileData - { name, lastName, email, gender, birthdate, etc. }
 * @returns {Promise<Object>}
 */
export const updateMyProfile = async (profileData) => {
  try {
    const response = await http.put(ENDPOINTS.CANDIDATES.ME, profileData);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ==================== ID/Phone-based endpoints (for admin/chatbot) ====================

/**
 * Get another candidate's public profile — reachable by any signed-in candidate, no
 * job/application relationship required (e.g. from a job-group post/comment author).
 * Deliberately narrower than a full CV: name/photo + summary/education/workExperience/
 * skills/hobbies only, no contact fields. See ENDPOINTS.CANDIDATES.PUBLIC_PROFILE.
 * @param {number} candidateId
 * @returns {Promise<Object>}
 */
export const getPublicProfile = async (candidateId) => {
  try {
    const response = await http.get(ENDPOINTS.CANDIDATES.PUBLIC_PROFILE(candidateId));
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Update candidate profile by ID
 * @param {number} candidateId
 * @param {Object} profileData - { name, lastName, email, phoneNumber, gender, birthdate, etc. }
 * @returns {Promise<Object>}
 */
export const updateCandidateProfile = async (candidateId, profileData) => {
  try {
    const response = await http.put(ENDPOINTS.CANDIDATES.UPDATE_PROFILE(candidateId), profileData);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Search for candidate by phone or email
 * @param {Object} params - { phone?, email? }
 * @returns {Promise<Object>}
 */
export const searchCandidate = async (params) => {
  try {
    const response = await http.get(ENDPOINTS.CANDIDATES.SEARCH, params);
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Upload profile photo for candidate
 * @param {number} candidateId
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} - { success, data: { photoUrl }, status }
 */
export const uploadPhoto = async (candidateId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await http.postFormData(
      ENDPOINTS.CANDIDATES.UPLOAD_PHOTO(candidateId),
      formData
    );
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

const candidateService = {
  // JWT-based (recommended)
  getMyProfile,
  updateMyProfile,
  // ID-based
  getPublicProfile,
  updateCandidateProfile,
  searchCandidate,
  uploadPhoto
};

export default candidateService;
